import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { loadAnalyticsTags, loadMarketingTags } from './consentTags';
import {
  isConsentCurrent,
  isConsentRequiredRegion,
  isDoNotTrackEnabled,
  readStoredConsent,
  writeStoredConsent,
  type ConsentPreferences,
} from './consentStore';

interface ConsentContextValue {
  /** True while the banner should be shown (undecided EU visitor or reopened). */
  bannerVisible: boolean;
  /** False for a first-time EU visitor who must choose; true when reopened. */
  dismissible: boolean;
  /** The last saved / default preferences, used to seed the manage toggles. */
  preferences: ConsentPreferences;
  /** Persist a decision, (re)load permitted tags, and close the banner. */
  savePreferences: (prefs: ConsentPreferences) => void;
  /** Reopen the banner in "manage" mode (from the footer control). */
  openPreferences: () => void;
  /** Dismiss the manage panel without changing a stored decision. */
  closePreferences: () => void;
}

const ALL_GRANTED: ConsentPreferences = { analytics: true, marketing: true };
const NONE_GRANTED: ConsentPreferences = { analytics: false, marketing: false };

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  // Undecided EU visitor → banner shown until they choose.
  const [needsDecision, setNeedsDecision] = useState(false);
  // Footer "Cookie settings" reopens the panel even after a decision.
  const [manageOpen, setManageOpen] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>(NONE_GRANTED);
  // What we've actually loaded this page session (to detect revocation).
  const applied = useRef<ConsentPreferences>(NONE_GRANTED);

  const applyConsent = useCallback((prefs: ConsentPreferences) => {
    if (prefs.analytics) loadAnalyticsTags();
    if (prefs.marketing) loadMarketingTags();
    applied.current = {
      analytics: applied.current.analytics || prefs.analytics,
      marketing: applied.current.marketing || prefs.marketing,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const stored = readStoredConsent();
    if (isConsentCurrent(stored)) {
      // An explicit prior decision (against the current version) wins over any
      // browser signal.
      setPreferences({ analytics: stored.analytics, marketing: stored.marketing });
      applyConsent(stored);
      return () => {
        cancelled = true;
      };
    }

    // Honour Do Not Track / Global Privacy Control as an opt-out of all
    // non-essential cookies, everywhere. We don't persist it (it's a live
    // browser signal), and we don't prompt — the visitor can still opt in via
    // the footer "Cookie settings", which records an explicit choice.
    if (isDoNotTrackEnabled()) {
      setPreferences(NONE_GRANTED);
      return () => {
        cancelled = true;
      };
    }

    // No valid stored decision — only block for detected EU/EEA/UK/CH visitors.
    isConsentRequiredRegion().then((required) => {
      if (cancelled) return;
      if (required) {
        setPreferences(NONE_GRANTED);
        setNeedsDecision(true);
      } else {
        // Non-EU (or region unknown): keep the load-by-default behaviour.
        setPreferences(ALL_GRANTED);
        applyConsent(ALL_GRANTED);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [applyConsent]);

  const savePreferences = useCallback(
    (prefs: ConsentPreferences) => {
      writeStoredConsent(prefs);
      setPreferences(prefs);
      setNeedsDecision(false);
      setManageOpen(false);

      // Scripts can't be unloaded mid-session; if the visitor revoked a
      // category we'd already loaded, reload so those tags stop firing.
      const revoked =
        (applied.current.analytics && !prefs.analytics) ||
        (applied.current.marketing && !prefs.marketing);
      if (revoked) {
        window.location.reload();
        return;
      }
      applyConsent(prefs);
    },
    [applyConsent],
  );

  const value = useMemo<ConsentContextValue>(
    () => ({
      bannerVisible: needsDecision || manageOpen,
      dismissible: !needsDecision,
      preferences,
      savePreferences,
      openPreferences: () => setManageOpen(true),
      closePreferences: () => setManageOpen(false),
    }),
    [needsDecision, manageOpen, preferences, savePreferences],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
}
